import { useRef, useState } from 'react';
import { Plus, ChevronDown, Home, HardDrive, Users, Trash2, Building2, PieChart } from '../../icons';
import { formatBytes, type NodePublic, type StorageUsage, type UsageSource } from '../../api/workspace';
import { tWorkspace, type WorkspaceI18nKey } from '../../lib/workspace-i18n';
import { FolderTree } from './FolderTree';
import { smartLabelOf, smartMetaOf } from './WorkspaceSmartFolders';
import { WorkspaceCreateMenu, type CreateAction } from './WorkspaceCreateMenu';
import { ListPane, ListPaneItem } from '../../components/shared';

/**
 * 좌측 상세 메뉴 (2026-08-14) — 사용자 요구:
 *
 *   "좌측 상세 메뉴에 상단에 '+ 만들기 또는 업로드' 버튼을 만들고 … 그 아래는 홈, 내 파일,
 *    공유됨, 휴지통이 있으며 제일 아래는 스토리지 사용량이 나오는 디자인"
 *
 * 종전 좌측은 `FolderTree` 하나뿐이었다(루트 바로가기 + 폴더 목록). 그 트리는 그대로 두고
 * **감싸는 셸**을 새로 만든다 — 트리 컴포넌트는 한 줄도 고치지 않는다(회귀 0).
 *
 * 스크롤 구조가 중요하다: 만들기 버튼과 용량 표시는 **스크롤 밖**에 고정하고 가운데 네비만
 * 스크롤시킨다. 예전처럼 패널 전체에 `overflow-y:auto` 를 걸면 폴더가 많아졌을 때 만들기
 * 버튼이 위로 밀려 사라진다.
 *
 * ── 2026-09-07 · 정본 A8 `ListPane` 채택 (사용자 결정 「안 A」) ──────────────────
 *
 * ★★★ **이 좌측만 «떠 있는 카드» 였다.** 게시판 · 근태 · 인사 · 결재의 좌측은 셸에 붙은
 *    판(오른쪽 실선 한 줄 · 240)인데, 여기만 사방 여백 12 · 네 변 테두리 · 반경 12 · 폭 232
 *    였다. 같은 자리에서 같은 일(어디로 갈지 고르기)을 하는 것이 혼자 다른 물건이었다.
 *
 * ★★★ **위에 적어 둔 다섯 조각이 A8 의 다섯 층에 하나씩 그대로 들어맞았다** —
 *    만들기 = ② 동작 · 네비 + 폴더 트리 = ④ 목록 · 용량 = ⑤ 발. 새로 만들 것이 없었고,
 *    「스크롤 밖 고정」이라는 위 요구도 A8 이 이미 그렇게 돼 있다.
 *
 * ★★★ **발(⑤)은 「단추 줄」이 아니다 — 화면이 정한다.** A8 을 처음 적을 때 게시판 하나만
 *    보고 「휴지통 · 만들기」라고 좁게 적었는데, 여기 발은 용량 위젯이다. 규격대로 단추로
 *    줄이면 이 화면이 유일하게 갖고 있던 정보가 사라진다 — 정본 쪽을 넓혔다(2026-09-07).
 *
 * ⚠ **찾기(③)는 아직 없다.** 폴더 목록을 `FolderTree` 가 제 안에서 지연 적재하므로,
 *   「스물 넘을 때만 찾기」(A2)를 주려면 그 소유를 끌어올려야 한다 — 트리는 한 줄도 안
 *   고친다는 이 파일의 규약과 부딪힌다. 머리의 개수도 같은 이유로 비워 두었다
 *   (`count` 는 「0」과 「모른다」가 다르므로, 모르면 안 그린다).
 */

/** 좌측 네비의 현재 화면. WorkspacePage 가 소유하고 여기로 내려온다. */
export type WorkspaceView = 'home' | 'my' | 'shared' | 'shared-by-me' | 'trash' | 'company';

interface NavSpec {
  view: WorkspaceView;
  icon: typeof Home;
  labelKey: WorkspaceI18nKey;
}

/**
 * 2026-08-14 (2차) — '회사 문서' 를 **'공유됨' 바로 아래**로 옮겼다(사용자 요구).
 *
 * 예전에는 구분선 아래 따로 떨어져 있었다. 회사 문서는 결국 "전사로 공유된 것" 이라
 * 공유됨과 같은 계열이고, 구분선 밑에 혼자 놓이면 별개의 저장소처럼 읽힌다.
 * 휴지통은 성격이 달라 계속 맨 아래(구분선 다음)에 둔다.
 */
const PRIMARY_NAV: ReadonlyArray<NavSpec> = [
  { view: 'home', icon: Home, labelKey: 'workspace.nav.home' },
  { view: 'my', icon: HardDrive, labelKey: 'workspace.nav.my' },
  { view: 'shared', icon: Users, labelKey: 'workspace.nav.shared' },
  { view: 'company', icon: Building2, labelKey: 'workspace.nav.company' },
];

interface Props {
  view: WorkspaceView;
  onViewChange: (view: WorkspaceView) => void;
  /** '내 파일' 에서만 펼치는 폴더 트리의 현재 선택. */
  activeFolderId: string | null;
  /** 끌어다 트리의 폴더에 놓기 (2026-08-18) — 페이지가 실제 이동을 수행한다. */
  onFolderDropMove?: (nodeIds: readonly string[], folderId: string | null) => void;
  /** 바깥에서 끌어온 파일을 트리의 폴더에 올린다 (2026-08-19). */
  onFolderDropFiles?: (folderId: string | null, folderName: string, files: File[]) => void;
  onFolderSelect: (folder: NodePublic | null) => void;
  /**
   * 트리를 다시 읽게 하는 신호 (2026-08-14, 기본 0).
   *
   * 트리는 mount 시 1회만 조회하고 캐시해서, 폴더를 만들거나 지워도 좌측은 그대로였다.
   * 부모가 목록을 바꾼 뒤 이 값을 올린다.
   */
  /** 2026-10-15 — 「내 파일」 폴더 줄 끝 「…」 메뉴(본문 목록과 같은 줄 메뉴를 띄운다). */
  onFolderMenu?: (folder: NodePublic, anchor: HTMLElement) => void;
  treeVersion?: number;
  /** 트리를 다시 읽을 때 펼쳐 보일 폴더 경로 (2026-10-27 · `FolderTree.revealIds`). */
  treeRevealIds?: readonly string[];
  /** 목록 응답에 실려 온 개인 사용량. 아직 없으면 placeholder 를 보여 준다. */
  usage: StorageUsage | null;
  onCreateAction: (action: CreateAction) => void;
  /**
   * 지금 위치에 만들 수 있는가 (2026-08-14, 기본 true).
   *
   * 회사 공유 폴더에서 일반 사원은 서버가 403 을 준다. 버튼을 **감추지 않고 비활성**으로
   * 두는 이유는, 좌측 최상단의 가장 큰 버튼이 사라지면 레이아웃이 통째로 달라 보여
   * "메뉴가 없어졌다" 로 읽히기 때문이다. 사유는 title 로 붙인다.
   */
  canCreate?: boolean;
  /** 비활성 사유 — 화면마다 다르므로 문구는 부모가 준다. */
  createDisabledHint?: string;
  /**
   * 출처별 모음 (2026-08-14) — '내 파일' 아래에 '공유됨' 과 **같은 모양**으로 편다.
   *
   * 본문 위 칩 줄에 있던 것을 여기로 옮겼다. 칩은 (a) 목록 세로를 먹고 (b) 출처를 고르면
   * 서버가 목록을 안 줘서 **칩 자체가 사라져 되돌아갈 수 없었다**. 좌측 네비는 목록 응답과
   * 무관하게 남으므로 두 문제가 함께 없어진다.
   */
  smartFolders?: ReadonlyArray<{ source: UsageSource; count: number }>;
  /** null = 전체(내 파일). */
  smartSource?: UsageSource | null;
  onSmartSelect?: (source: UsageSource | null) => void;
  /**
   * 용량 관리 열기 (2026-08-14 2차, 선택).
   *
   * 안 넘기면 버튼 자체가 안 나온다 — 사이드바만 따로 쓰는 테스트가 모달 상태까지
   * 떠안지 않게 하려는 것이다.
   */
  onManageStorage?: () => void;
}

export function WorkspaceSidebar({
  view, onViewChange, activeFolderId, onFolderSelect, onFolderDropMove, onFolderDropFiles, usage, onCreateAction,
  canCreate = true, createDisabledHint, treeVersion = 0, onFolderMenu, treeRevealIds,
  smartFolders = [], smartSource = null, onSmartSelect, onManageStorage,
}: Props) {
  const createBtnRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // 사용량 막대 — 기존 헤더 카드와 같은 규칙을 쓴다(색 규칙이 이미 CSS 에 있다).
  const level: 'normal' | 'warning' | 'danger' =
    !usage || usage.unlimited ? 'normal'
      : usage.pct >= 100 ? 'danger'
        : usage.warn ? 'warning' : 'normal';
  const barWidth = !usage || usage.unlimited ? 0 : Math.min(100, usage.pct);
  const remaining = usage && !usage.unlimited
    ? Math.max(0, usage.limitBytes - usage.usedBytes)
    : null;

  /*
   * ② 동작 — 「만들기 또는 업로드」. 하루에도 여러 번 하는 일이라 머리 바로 아래다(P3-4).
   * ★ 높이는 CSS 에서 `--h-lg`(40) 로 못 박았다. 지금까지는 여백이 만든 40.3 이라
   *   「값은 맞는데 근거가 없는」 상태였다 — 격자가 움직이면 여기만 남는다.
   */
  const createAction = (
    <button
      type="button"
      ref={createBtnRef}
      className="ws-create-btn"
      aria-haspopup="menu"
      aria-expanded={menuOpen}
      disabled={!canCreate}
      {...(!canCreate && createDisabledHint ? { title: createDisabledHint } : {})}
      onClick={() => setMenuOpen((v) => !v)}
    >
      <Plus size={17} aria-hidden />
      {tWorkspace('workspace.create.button')}
      <ChevronDown size={13} aria-hidden className="ws-create-caret" />
    </button>
  );

  /* ⑤ 발 — 용량. A8 의 발은 «화면이 정하는 자리»다(정본 2026-09-07). */
  const storageFooter = (
    <div className={`ws-side-foot ws-side-foot--${level}`}>
      <div className="ws-storage-row">
        <b>{tWorkspace('workspace.storage.title')}</b>
        <span className="ws-num">
          {usage ? formatBytes(usage.usedBytes) : '—'}
          {' / '}
          {!usage ? '—' : usage.unlimited
            ? tWorkspace('workspace.storage.unlimited')
            : formatBytes(usage.limitBytes)}
        </span>
      </div>
      <div className="ws-storage-bar">
        <div className="ws-storage-fill" style={{ width: `${barWidth}%` }} />
      </div>
      <p className="ws-storage-note">
        {remaining === null
          ? (usage
            ? tWorkspace('workspace.storage.unlimited')
            : tWorkspace('workspace.storage.remaining_pending'))
          : tWorkspace('workspace.storage.remaining', { size: formatBytes(remaining) })}
      </p>
      {/*
        2026-08-14 — 한도 임박·초과 문구. 상단 스토리지 카드를 없애면서 이리로 옮겼다.
        막대 색만으로는 "왜 업로드가 막히는지" 를 알 수 없다.
      */}
      {level !== 'normal' && (
        <p className="ws-storage-alert" role="status">
          {tWorkspace(level === 'danger' ? 'workspace.storage.over' : 'workspace.storage.warn')}
        </p>
      )}
      {/*
        용량 관리 (2026-08-14 (2차) 사용자 요구) — 막대는 "얼마나 찼나" 만 말해 준다.
        정작 알아야 하는 것은 **어디가 무겁나**이고, 그건 폴더별로 굴려 봐야 나온다.
        여는 것은 부모가 한다(모달 상태를 사이드바가 들고 있을 이유가 없다).
      */}
      {onManageStorage && (
        <button type="button" className="ws-storage-manage" onClick={onManageStorage}>
          <PieChart size={13} aria-hidden />
          {tWorkspace('workspace.storage.manage')}
        </button>
      )}
    </div>
  );

  return (
    <>
      <ListPane
        className="ws-side"
        title={tWorkspace('workspace.title')}
        ariaLabel={tWorkspace('workspace.tree.aria')}
        action={createAction}
        footer={storageFooter}
      >
        {PRIMARY_NAV.map((n) => {
          const Icon = n.icon;
          // '공유됨' 은 방향이 둘이다 — 나에게 공유된 것과 내가 공유한 것.
          // 지금까지 후자를 볼 화면이 없어서 무엇을 누구에게 공유했는지 확인·해제할 수 없었다.
          const sharedActive = n.view === 'shared' && (view === 'shared' || view === 'shared-by-me');
          return (
            <div key={n.view}>
              <ListPaneItem
                name={tWorkspace(n.labelKey)}
                icon={<Icon aria-hidden />}
                selected={n.view === 'shared' ? sharedActive : view === n.view}
                onClick={() => onViewChange(n.view)}
              />
              {/*
                '내 파일' 아래 출처별 모음 — '공유됨' 의 하위 탭과 같은 규칙이다.
                맨 위 '전체' 가 곧 내 파일 전체이고, 아래는 그 안에서 출처로 거른 것.
              */}
              {n.view === 'my' && view === 'my' && smartFolders.length > 0 && (
                <div className="ws-nav-sub" role="tablist">
                  <button
                    type="button" role="tab"
                    className="ws-nav-subitem"
                    aria-selected={smartSource === null}
                    onClick={() => onSmartSelect?.(null)}
                  >
                    {tWorkspace('workspace.smart.all')}
                  </button>
                  {smartFolders.map(({ source, count }) => {
                    // ⚠ 표에 없는 출처(서버가 새로 켠 것)도 반드시 그려져야 한다 —
                    //   예전 `SMART_META[source]` 직접 읽기는 undefined 로 좌측 네비를
                    //   통째로 터뜨렸다. smartMetaOf 가 기본 아이콘·이름을 보장한다.
                    const meta = smartMetaOf(source);
                    const SmartIcon = meta.icon;
                    return (
                      <button
                        key={source}
                        type="button" role="tab"
                        className="ws-nav-subitem"
                        aria-selected={smartSource === source}
                        onClick={() => onSmartSelect?.(source)}
                      >
                        <SmartIcon size={13} aria-hidden style={{ color: meta.tone }} />
                        {smartLabelOf(source)}
                        <span className="ws-nav-subcount ws-num">{count}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              {/*
                ★ 폴더 트리는 **'내 파일' 바로 아래**다 (2026-09-02, 사용자 요구:
                  "폴더 > 워크스페이스 하위에 폴더를 만드는 기능을 내 파일 > 폴더가
                   생기도록 수정한다").

                예전에는 휴지통 **아래**에 '폴더' 라는 별도 구획이 있었고 그 트리의 루트가
                '워크스페이스' 였다. 그래서 같은 것을 가리키는 입구가 둘('내 파일' 과
                '워크스페이스')이었고, 폴더가 내 파일과 무관한 다른 저장소처럼 읽혔다.
                이제 하나다 — 내 파일을 펴면 그 안에 내 폴더가 있다.

                루트 줄은 감춘다(`hideRoot`) — '내 파일' 항목 자체가 루트다.
              */}
              {n.view === 'my' && view === 'my' && (
                <div className="ws-nav-sub">
                  <FolderTree
                    activeFolderId={activeFolderId}
                    version={treeVersion}
                    {...(treeRevealIds ? { revealIds: treeRevealIds } : {})}
                    onSelect={onFolderSelect}
                    onDropMove={onFolderDropMove}
                    onDropFiles={onFolderDropFiles}
                    hideRoot
                    {...(onFolderMenu ? { onFolderMenu } : {})}
                  />
                </div>
              )}
              {n.view === 'shared' && sharedActive && (
                <div className="ws-nav-sub" role="tablist">
                  <button
                    type="button" role="tab"
                    className="ws-nav-subitem"
                    aria-selected={view === 'shared'}
                    onClick={() => onViewChange('shared')}
                  >
                    {tWorkspace('workspace.nav.shared_with_me')}
                  </button>
                  <button
                    type="button" role="tab"
                    className="ws-nav-subitem"
                    aria-selected={view === 'shared-by-me'}
                    onClick={() => onViewChange('shared-by-me')}
                  >
                    {tWorkspace('workspace.nav.shared_by_me')}
                  </button>
                </div>
              )}
            </div>
          );
        })}

        <div className="ws-nav-sep" />

        {/* 휴지통은 성격이 달라 구분선 아래에 홀로 둔다(회사 문서는 위 PRIMARY_NAV 로 옮겼다). */}
        <ListPaneItem
          name={tWorkspace('workspace.nav.trash')}
          icon={<Trash2 aria-hidden />}
          selected={view === 'trash'}
          onClick={() => onViewChange('trash')}
        />

        {/*
          ⚠ 여기 있던 '폴더' 구획(라벨 + FolderTree)은 **'내 파일' 아래로 옮겼다**
            (2026-09-02). 휴지통 밑에 따로 있으면 내 파일과 다른 저장소처럼 보였다.
        */}
      </ListPane>

      {menuOpen && (
        <WorkspaceCreateMenu
          anchorRef={createBtnRef}
          onSelect={onCreateAction}
          onClose={() => setMenuOpen(false)}
        />
      )}
    </>
  );
}
